import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';

export type SectionId =
  | 'initiation'
  | 'documentData'
  | 'counterparty'
  | 'reimbursement'
  | 'lineItems';

export const SECTION_FOCUS_ORDER: SectionId[] = [
  'initiation',
  'counterparty',
  'reimbursement',
  'documentData'
];

export type ScrollSectionId =
  | SectionId
  | 'documentPaymentStatus'
  | 'commentsAndTags';

export const SCROLL_SECTION_IDS: ScrollSectionId[] = [
  ...SECTION_FOCUS_ORDER,
  'documentPaymentStatus',
  'commentsAndTags'
];

export interface OverallConfidenceSection {
  id: string;
  fieldNames: string[];
}

export const SECTION_FIELDS: Record<SectionId, InvoiceVerificationFieldName[]> =
  {
    initiation: [
      InvoiceVerificationFieldName.CompanyId,
      InvoiceVerificationFieldName.DocumentType
    ],
    documentData: [
      InvoiceVerificationFieldName.InvoiceNumber,
      InvoiceVerificationFieldName.InvoiceDate,
      InvoiceVerificationFieldName.InvoiceAmount,
      InvoiceVerificationFieldName.VatBase,
      InvoiceVerificationFieldName.VatAmount,
      InvoiceVerificationFieldName.CurrencyId
    ],
    counterparty: [
      InvoiceVerificationFieldName.CounterpartyId,
      InvoiceVerificationFieldName.CounterpartyName,
      InvoiceVerificationFieldName.CounterpartyRegistrationNumber,
      InvoiceVerificationFieldName.CounterpartyVatNumber
    ],
    reimbursement: [
      InvoiceVerificationFieldName.ReimbursementCounterpartyId,
      InvoiceVerificationFieldName.ReimbursementCounterpartyName,
      InvoiceVerificationFieldName.ReimbursementIban
    ],
    // The line items section is read-only — it renders extracted data and has
    // no editable fields, so it contributes nothing to validation.
    lineItems: []
  };

export const SECTION_REQUIRED_FIELDS: Record<
  SectionId,
  InvoiceVerificationFieldName[]
> = {
  initiation: [
    InvoiceVerificationFieldName.CompanyId,
    InvoiceVerificationFieldName.DocumentType
  ],
  documentData: [
    InvoiceVerificationFieldName.InvoiceNumber,
    InvoiceVerificationFieldName.InvoiceDate,
    InvoiceVerificationFieldName.InvoiceAmount,
    InvoiceVerificationFieldName.VatBase,
    InvoiceVerificationFieldName.VatAmount,
    InvoiceVerificationFieldName.CurrencyId
  ],
  counterparty: [InvoiceVerificationFieldName.CounterpartyName],
  reimbursement: [InvoiceVerificationFieldName.ReimbursementCounterpartyId],
  lineItems: []
};

export const CONFIDENCE_SECTIONS: OverallConfidenceSection[] = [
  { id: 'initiation', fieldNames: ['companyId', 'documentType'] },
  {
    id: 'counterparty',
    fieldNames: [
      'counterpartyId',
      'counterpartyName',
      'counterpartyRegistrationNumber',
      'counterpartyVatNumber'
    ]
  },
  { id: 'reimbursement', fieldNames: ['reimbursementCounterpartyId'] },
  {
    id: 'documentData',
    fieldNames: [
      'invoiceNumber',
      'invoiceDate',
      'invoiceAmount',
      'vatBase',
      'vatAmount',
      'currencyId'
    ]
  }
];

export const PARTICIPANT_SECTION_IDS = ['counterparty', 'reimbursement'];
export const NON_PARTICIPATING_SECTION_IDS = ['commentsAndTags', 'lineItems'];

export const getRequiredFieldsForSection = (
  sectionId: SectionId,
  allowExpenseType: boolean
): InvoiceVerificationFieldName[] => {
  const requiredFields = [...SECTION_REQUIRED_FIELDS[sectionId]];
  if (sectionId === 'documentData' && allowExpenseType) {
    requiredFields.push(InvoiceVerificationFieldName.ExpenseType);
  }
  return requiredFields;
};

export const getActiveSectionIds = (isReimbursement: boolean): SectionId[] =>
  (Object.keys(SECTION_FIELDS) as SectionId[]).filter((sectionId) => {
    if (sectionId === 'counterparty') {
      return !isReimbursement;
    }
    if (sectionId === 'reimbursement') {
      return isReimbursement;
    }
    return true;
  });
